import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Download, Gift, ArrowRight, Check, Calendar } from "lucide-react";

// 12 months in chronological Jan-Dec 2026 order.
// calendar-1.png = July, calendar-8.png used to be a July duplicate (skipped),
// calendar-13.png = December (Wholeness).
const calendarPages = [
  "/calendar-2.png",  // January — New Beginnings
  "/calendar-3.png",  // February — Self-Love
  "/calendar-4.png",  // March — Renewal
  "/calendar-5.png",  // April — Mind Body Spirit
  "/calendar-6.png",  // May — Sacred Home
  "/calendar-7.png",  // June — Inner Wisdom
  "/calendar-1.png",  // July — Release & Flow
  "/calendar-9.png",  // August — Authentic Self
  "/calendar-10.png", // September — Intentional Living
  "/calendar-11.png", // October — Mindful Thought
  "/calendar-12.png", // November — Gratitude
  "/calendar-13.png", // December — Wholeness
];

export default function ThankYou() {
  useEffect(() => { document.title = "Thank You — Your Free Calendar Is Ready | Mind Body Spirit HYGIENE™ Tools"; }, []);
  const handlePrint = () => {
    // Build print content via an iframe + srcDoc — safer than document.write.
    // calendarPages contains only static literal image paths (no user input),
    // so this is XSS-safe by data flow, but we use srcDoc as belt-and-braces.
    const imagesHtml = calendarPages
      .map(
        (src) =>
          `<div style="page-break-after: always; display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 0; margin: 0;">
            <img src="${src}" alt="Printable card page" style="max-width: 100%; max-height: 100vh; object-fit: contain;" />
          </div>`
      )
      .join("");

    const html =
      "<!doctype html><html><head><title>365 Days of I GET TO Calendar</title></head>" +
      '<body style="margin:0; padding:0;">' +
      imagesHtml +
      "</body></html>";

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.srcdoc = html;
    document.body.appendChild(iframe);

    iframe.addEventListener("load", () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error("Print failed:", err);
      }
      // Clean up after a delay so the browser dialog has time to open
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 2000);
    });
  };

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="thank-you-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/downloads/MBSH-logo-TM-e4476e.png"
              alt="Mind Body Spirit HYGIENE™ Tools"
              className="h-12 w-auto"
            />
          </Link>
          <nav className="flex items-center gap-4 md:gap-8">
            <Link to="/" className="font-sans text-[#068fa2] hover:text-[#25a1c2] transition-colors text-sm">Home</Link>
            <Link to="/build-your-own" className="text-[#068fa2] hover:text-[#25a1c2] text-sm font-medium transition-colors" data-testid="thankyou-nav-build-your-own">Build Your Own</Link>
            <Link to="/" className="bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-5 py-2.5 font-sans font-medium text-sm transition-all">
              Shop Products
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="w-20 h-20 rounded-full bg-[#25a1c2]/10 flex items-center justify-center mx-auto mb-6">
            <Gift className="w-10 h-10 text-[#25a1c2]" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter text-[#042F2E] mb-5">
            Welcome to the Journey!
          </h1>
          <p className="font-sans text-base md:text-lg text-[#068fa2] max-w-xl mx-auto mb-4">
            You're officially part of the "I GET TO" family. Check your email for your first weekly message from Temple.
          </p>
          <p className="font-sans text-[#25a1c2] font-medium text-lg">
            But first — here's your FREE gift!
          </p>
        </div>
      </section>

      {/* Free Gift Download */}
      <section className="pb-16 md:pb-20" data-testid="calendar-download-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-br from-[#068fa2] to-[#25a1c2] rounded-3xl overflow-hidden shadow-xl">
            <div className="flex flex-col lg:flex-row">
              {/* Calendar Preview */}
              <div className="lg:w-2/5 relative overflow-hidden bg-[#042F2E] flex items-center justify-center p-8">
                <div className="relative">
                  <img
                    src="/calendar-1.png"
                    alt="365 Days of I GET TO Calendar"
                    className="w-full max-w-xs rounded-xl shadow-2xl transform -rotate-3"
                  />
                  <img
                    src="/calendar-2.png"
                    alt="Calendar Preview"
                    className="absolute top-4 left-4 w-full max-w-xs rounded-xl shadow-xl transform rotate-3 opacity-60"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="lg:w-3/5 p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-[#5EEAD4]" />
                  <span className="font-sans text-sm text-[#5EEAD4] font-medium tracking-wide uppercase">
                    Your Free Gift
                  </span>
                </div>

                <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-white mb-4">
                  365 Days of "I GET TO"<br />Printable Calendar
                </h2>

                <p className="font-sans text-white/80 text-sm leading-relaxed mb-6">
                  A beautiful 12-month calendar with daily "I GET TO" affirmations for every single day.
                  Print it, hang it, and start each day with intention.
                </p>

                <ul className="space-y-2 mb-8">
                  {[
                    "12 months of daily affirmations (July 2026 – June 2027)",
                    "Unique monthly themes (New Beginnings, Self-Love, Joy, Freedom & more)",
                    "Print at home — hang on your wall or fridge",
                    "Matches your 52-week email journey perfectly",
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                      <Check className="w-4 h-4 text-[#5EEAD4] mt-0.5 shrink-0" />
                      <span className="font-sans">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handlePrint}
                  data-testid="download-calendar-btn"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#F0FDF4] text-[#25a1c2] rounded-full px-8 py-4 font-sans font-semibold transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl w-full lg:w-auto"
                >
                  <Download className="w-5 h-5" />
                  Print / Save Calendar as PDF
                </button>

                <p className="font-sans text-white/50 text-xs mt-4">
                  Click the button above → Choose "Save as PDF" in the print dialog
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Next */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#042F2E] text-center mb-10">
            What Happens Next?
          </h2>

          <div className="space-y-6">
            {[
              {
                step: "1",
                title: "Check your email",
                desc: "Your first weekly 'I GET TO' message from Temple is on its way. Check your inbox (and spam folder just in case).",
              },
              {
                step: "2",
                title: "Print your calendar",
                desc: "Download and print your free 365-day calendar. Hang it somewhere you'll see it every morning.",
              },
              {
                step: "3",
                title: "Start practicing",
                desc: "Every time you catch yourself saying 'I have to,' pause and reframe it to 'I get to.' That's it. Three simple words.",
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-6 items-start">
                <span className="font-serif text-4xl text-[#CCFBF1] w-10 shrink-0">{item.step}</span>
                <div>
                  <h3 className="font-sans font-bold text-[#042F2E] mb-1">{item.title}</h3>
                  <p className="font-sans text-[#475569] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shop CTA */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-[#F0F5F4] rounded-3xl p-8 md:p-12">
            <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">
              Ready to go deeper?
            </p>
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#042F2E] mb-5">
              Explore Our Digital Products
            </h2>
            <p className="font-sans text-[#475569] mb-8 max-w-md mx-auto">
              Journals, workbooks, affirmation cards, and ritual guides designed to deepen your "I GET TO" practice.
            </p>
            <Link
              to="/"
              data-testid="shop-cta-btn"
              className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-sans font-medium transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
            >
              Browse the Shop
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
    </div>
  );
}
